import { useEffect } from 'react';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import FloatingButtons from '../../../components/feature/FloatingButtons';
import Breadcrumb from '../../../components/feature/Breadcrumb';
import InternalLinks from '../../../components/feature/InternalLinks';
import FAQSchema from '../../../components/feature/FAQSchema';
import { Link } from 'react-router-dom';

export default function UmraniyePage() {
  useEffect(() => {
    document.title = 'Ümraniye Fiber Optik Altyapı ve Güvenlik Sistemleri | Fiber Ek';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Ümraniye\'de fiber optik altyapı kurulumu, IP kamera sistemleri, yangın alarm ve güvenlik çözümleri. Kaliteli hizmet. ☎ +90 553 716 55 61');
    }

    // Canonical link
    const canonical = document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = `${import.meta.env.VITE_SITE_URL}/bolgeler/umraniye`;
    document.head.appendChild(canonical);

    // Open Graph tags
    const ogTitle = document.createElement('meta');
    ogTitle.setAttribute('property', 'og:title');
    ogTitle.content = 'Ümraniye Fiber Optik Altyapı ve Güvenlik Sistemleri | Fiber Ek';
    document.head.appendChild(ogTitle);

    const ogDescription = document.createElement('meta');
    ogDescription.setAttribute('property', 'og:description');
    ogDescription.content = 'Ümraniye\'de fiber optik altyapı kurulumu, IP kamera sistemleri, yangın alarm ve güvenlik çözümleri. Kaliteli hizmet. ☎ +90 553 716 55 61';
    document.head.appendChild(ogDescription);

    const ogUrl = document.createElement('meta');
    ogUrl.setAttribute('property', 'og:url');
    ogUrl.content = `${import.meta.env.VITE_SITE_URL}/bolgeler/umraniye`;
    document.head.appendChild(ogUrl);

    const ogType = document.createElement('meta');
    ogType.setAttribute('property', 'og:type');
    ogType.content = 'website';
    document.head.appendChild(ogType);

    const ogLocale = document.createElement('meta');
    ogLocale.setAttribute('property', 'og:locale');
    ogLocale.content = 'tr_TR';
    document.head.appendChild(ogLocale);

    // JSON-LD Schema
    const schema = document.createElement('script');
    schema.type = 'application/ld+json';
    schema.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Fiber Ek - Ümraniye",
      "description": "Ümraniye'de fiber optik altyapı kurulumu, IP kamera sistemleri, yangın alarm ve güvenlik çözümleri",
      "areaServed": "Ümraniye",
      "telephone": "+905537165561",
      "url": `${import.meta.env.VITE_SITE_URL}/bolgeler/umraniye`,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Ümraniye",
        "addressRegion": "İstanbul",
        "addressCountry": "TR"
      }
    });
    document.head.appendChild(schema);

    return () => {
      document.head.removeChild(canonical);
      document.head.removeChild(ogTitle);
      document.head.removeChild(ogDescription);
      document.head.removeChild(ogUrl);
      document.head.removeChild(ogType);
      document.head.removeChild(ogLocale);
      document.head.removeChild(schema);
    };
  }, []);

  const faqData = [
    {
      question: 'Ümraniye\'de fiber optik kablo tamiri hizmeti veriyor musunuz?',
      answer: 'Evet, Ümraniye\'nin tüm bölgelerinde 7/24 acil fiber optik kablo tamiri hizmeti sunuyoruz. Fusion splicing, OTDR test ve fiber sonlandırma işlemlerini profesyonel ekipmanlarla gerçekleştiriyoruz.'
    },
    {
      question: 'Ümraniye\'de hangi güvenlik sistemlerini kuruyorsunuz?',
      answer: 'Ümraniye\'de IP kamera sistemleri, hırsız alarm sistemleri, yangın alarm sistemleri, geçiş kontrol sistemleri, turnike ve bariyer sistemleri kurulumu yapıyoruz. Sanayi tesisleri ve plaza binaları için özel çözümler sunuyoruz.'
    },
    {
      question: 'Ümraniye OSB\'de fabrika fiber altyapı kurulumu yapıyor musunuz?',
      answer: 'Evet, Ümraniye Organize Sanayi Bölgesi\'nde fabrika fiber altyapı kurulumu, metro ethernet fiber ek, OM3/OM4 fiber ek ve SC LC konnektör sonlandırma hizmetleri veriyoruz.'
    },
    {
      question: 'Acil fiber arıza durumunda Ümraniye\'ye ne kadar sürede geliyorsunuz?',
      answer: 'Ümraniye bölgesinde acil fiber arıza çağrılarına ortalama 30-45 dakika içinde müdahale ediyoruz. 7/24 kesintisiz destek hattımız her zaman hizmetinizdedir.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      <FAQSchema faqs={faqData} />
      <Navbar />
      
      <div className="pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-6xl">
          <Breadcrumb 
            items={[
              { label: 'Ana Sayfa', path: '/' },
              { label: 'Bölgeler', path: '/#locations' },
              { label: 'Ümraniye' }
            ]} 
          />

          {/* Hero Section */}
          <div className="relative rounded-2xl overflow-hidden mb-16 h-[400px]">
            <img 
              src="https://public.readdy.ai/ai/img_res/cf9344dd7e332c9e58526f85268a167c.jpg"
              alt="Ümraniye Fiber Optik ve Güvenlik Sistemleri"
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/60"></div>
            <div className="relative h-full flex items-center">
              <div className="container mx-auto px-4">
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                  Ümraniye Fiber Optik ve Güvenlik Sistemleri
                </h1>
                <p className="text-xl text-gray-200 mb-8 max-w-3xl">
                  Ümraniye OSB ve sanayi bölgelerinde profesyonel fiber kablo tamiri, fusion splicing ve güvenlik sistemleri kurulumu
                </p>
                <div className="flex flex-wrap gap-4">
                  <a 
                    href="tel:+905537165561"
                    className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-phone-line text-xl"></i>
                    Hemen Ara
                  </a>
                  <a 
                    href="https://wa.me/905537165561"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-whatsapp-line text-xl"></i>
                    WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            <div className="lg:col-span-2 space-y-8">
              {/* Fiber Optik Hizmetleri */}
              <section className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-8 border border-slate-700/50">
                <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                  <i className="ri-flashlight-line text-teal-400"></i>
                  Ümraniye Fiber Optik Hizmetleri
                </h2>
                <div className="prose prose-invert max-w-none">
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Ümraniye bölgesinde <strong>fiber kablo tamiri</strong>, <strong>fiber optik sonlandırma</strong> ve <strong>fusion splicing hizmeti</strong> konusunda uzman ekibimizle hizmet veriyoruz. Ümraniye OSB ve sanayi bölgelerinde 7/24 <strong>acil fiber arıza</strong> müdahale hizmeti sunuyoruz.
                  </p>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    <strong>OTDR test hizmeti</strong> ile fiber optik hatlarınızın performansını ölçüyor, arızaları tespit ediyor ve profesyonel ekipmanlarımızla onarım yapıyoruz. Ümraniye OSB\'de faaliyet gösteren fabrikalar için <strong>fabrika fiber altyapı</strong> kurulumu, <strong>metro ethernet fiber ek</strong> ve <strong>bina fiber kablo tamiri</strong> hizmetleri veriyoruz.
                  </p>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    <strong>SC LC konnektör sonlandırma</strong>, <strong>OM3 OM4 fiber ek</strong> işlemleri ve tüm fiber optik altyapı çözümlerinde deneyimli kadromuzla yanınızdayız. Ümraniye fiber ustası arıyorsanız, profesyonel ekibimiz her türlü <strong>fiber ek hizmeti</strong> için hazır.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mt-6">
                  <Link to="/fiber-services/fiber-cable-repair" className="flex items-center gap-3 p-4 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors cursor-pointer">
                    <i className="ri-tools-line text-2xl text-teal-400"></i>
                    <span className="text-white font-medium">Fiber Kablo Tamiri</span>
                  </Link>
                  <Link to="/fiber-services/fiber-termination" className="flex items-center gap-3 p-4 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors cursor-pointer">
                    <i className="ri-link text-2xl text-teal-400"></i>
                    <span className="text-white font-medium">Fiber Sonlandırma</span>
                  </Link>
                  <Link to="/fiber-services/otdr-test" className="flex items-center gap-3 p-4 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors cursor-pointer">
                    <i className="ri-line-chart-line text-2xl text-teal-400"></i>
                    <span className="text-white font-medium">OTDR Test</span>
                  </Link>
                  <Link to="/fiber-services/factory-fiber" className="flex items-center gap-3 p-4 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors cursor-pointer">
                    <i className="ri-building-line text-2xl text-teal-400"></i>
                    <span className="text-white font-medium">Fabrika Fiber Altyapı</span>
                  </Link>
                </div>
              </section>

              {/* Güvenlik Sistemleri */}
              <section className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-8 border border-slate-700/50">
                <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                  <i className="ri-shield-check-line text-teal-400"></i>
                  Ümraniye Güvenlik Sistemleri
                </h2>
                <div className="prose prose-invert max-w-none">
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Ümraniye bölgesinde <strong>IP kamera sistemleri</strong>, <strong>hırsız alarm sistemleri</strong> ve <strong>yangın alarm sistemleri</strong> kurulumu konusunda profesyonel hizmet sunuyoruz. Ümraniye OSB\'de faaliyet gösteren fabrikalar, sanayi tesisleri ve ticari işletmeler için özel güvenlik çözümleri geliştiriyoruz.
                  </p>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    <strong>Geçiş kontrol sistemleri</strong>, <strong>turnike sistemleri</strong> ve <strong>bariyer sistemleri</strong> ile işletmenizin güvenliğini en üst seviyeye çıkarıyoruz. Sanayi kamera sistemi kurulumu, plaka tanıma sistemleri ve 7/24 izleme çözümleri ile işletmenizi koruma altına alıyoruz.
                  </p>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Ümraniye OSB\'de fabrika güvenlik sistemi kurulumu, entegre alarm sistemleri ve profesyonel montaj hizmetleri sunuyoruz. Tüm güvenlik sistemlerimiz fiber optik altyapı ile entegre çalışarak yüksek performans ve güvenilirlik sağlar.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mt-6">
                  <Link to="/security-systems/ip-camera-systems" className="flex items-center gap-3 p-4 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors cursor-pointer">
                    <i className="ri-camera-line text-2xl text-teal-400"></i>
                    <span className="text-white font-medium">IP Kamera Sistemleri</span>
                  </Link>
                  <Link to="/security-systems/burglar-alarm-systems" className="flex items-center gap-3 p-4 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors cursor-pointer">
                    <i className="ri-alarm-line text-2xl text-teal-400"></i>
                    <span className="text-white font-medium">Hırsız Alarm</span>
                  </Link>
                  <Link to="/security-systems/fire-alarm-systems" className="flex items-center gap-3 p-4 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors cursor-pointer">
                    <i className="ri-fire-line text-2xl text-teal-400"></i>
                    <span className="text-white font-medium">Yangın Alarm</span>
                  </Link>
                  <Link to="/security-systems/turnstile-systems" className="flex items-center gap-3 p-4 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors cursor-pointer">
                    <i className="ri-door-line text-2xl text-teal-400"></i>
                    <span className="text-white font-medium">Turnike Sistemleri</span>
                  </Link>
                </div>
              </section>

              {/* FAQ Section */}
              <section className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-8 border border-slate-700/50">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                  <i className="ri-question-line text-teal-400"></i>
                  Sıkça Sorulan Sorular
                </h3>
                <div className="space-y-4">
                  {faqData.map((faq, index) => (
                    <details key={index} className="group bg-slate-700/30 rounded-lg p-4 cursor-pointer">
                      <summary className="font-semibold text-white list-none flex items-center justify-between">
                        {faq.question}
                        <i className="ri-arrow-down-s-line text-xl group-open:rotate-180 transition-transform"></i>
                      </summary>
                      <p className="text-gray-300 mt-3 leading-relaxed">{faq.answer}</p>
                    </details>
                  ))}
                </div>
              </section>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* İletişim Kartı */}
              <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl p-6 text-white sticky top-24">
                <h3 className="text-xl font-bold mb-4">Hemen İletişime Geçin</h3>
                <div className="space-y-4">
                  <a 
                    href="tel:+905537165561"
                    className="flex items-center gap-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg p-4 transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-phone-line text-2xl"></i>
                    <div>
                      <div className="text-sm opacity-90">Telefon</div>
                      <div className="font-semibold">+90 553 716 55 61</div>
                    </div>
                  </a>
                  <a 
                    href="https://wa.me/905537165561"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg p-4 transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-whatsapp-line text-2xl"></i>
                    <div>
                      <div className="text-sm opacity-90">WhatsApp</div>
                      <div className="font-semibold">Mesaj Gönder</div>
                    </div>
                  </a>
                  <div className="flex items-start gap-3 bg-white/10 rounded-lg p-4">
                    <i className="ri-time-line text-2xl mt-1"></i>
                    <div>
                      <div className="text-sm opacity-90">Çalışma Saatleri</div>
                      <div className="font-semibold">7/24 Hizmet</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Google Maps */}
          <section className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-8 border border-slate-700/50">
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <i className="ri-map-pin-line text-teal-400"></i>
              Ümraniye Konum
            </h3>
            <div className="w-full h-[450px] rounded-xl overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d96211.5!2d29.11!3d41.02!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cac7d7f7f7f7f7%3A0x7f7f7f7f7f7f7f7f!2zw5xtcmFuaXll!5e0!3m2!1str!2str!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Ümraniye Harita"
              ></iframe>
            </div>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
}